import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  Edit3,
  FileText,
  ImagePlus,
  Loader2,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { errMsg } from "@shared/api/errors";
import {
  CertificationButton,
  CertificationConfirmDialog,
  CertificationInputField,
  CertificationModal,
  CertificationSelectField,
} from "../../ui";
import { manualSistemaIntegradoApi } from "../api/manual-sistema-integrado";
import { ManualSistemaIntegradoVisualEditor, remarkManualTableColors } from "./ManualSistemaIntegradoVisualEditor";
import type {
  ManualSistemaIntegradoStructuredImage,
  ManualSistemaIntegradoStructuredSection,
  ManualSistemaIntegradoStructuredSectionPayload,
} from "../types/manual-sistema-integrado";

type Props = {
  canManage: boolean;
};

type EditorState = {
  open: boolean;
  row: ManualSistemaIntegradoStructuredSection | null;
  parentId: number | null;
};

type ImageDeleteTarget = {
  sectionId: number;
  image: ManualSistemaIntegradoStructuredImage;
};

const emptyPayload: ManualSistemaIntegradoStructuredSectionPayload = {
  parent_id: null,
  codigo: "",
  titulo: "",
  contenido: "",
  orden: 0,
};

function sectionLabel(row: ManualSistemaIntegradoStructuredSection): string {
  return row.codigo ? `${row.codigo} · ${row.titulo}` : row.titulo;
}

export function ManualSistemaIntegradoStructuredPanel({ canManage }: Props) {
  const [rows, setRows] = useState<ManualSistemaIntegradoStructuredSection[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>({ open: false, row: null, parentId: null });
  const [payload, setPayload] = useState<ManualSistemaIntegradoStructuredSectionPayload>(emptyPayload);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [deleteSection, setDeleteSection] = useState<ManualSistemaIntegradoStructuredSection | null>(null);
  const [deletingSection, setDeletingSection] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteImage, setDeleteImage] = useState<ImageDeleteTarget | null>(null);
  const [deletingImage, setDeletingImage] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const state = await manualSistemaIntegradoApi.getStructured();
      setRows(state.data);
      setSelectedId((current) => {
        if (current !== null && state.data.some((row) => row.id === current)) return current;
        return state.data.find((row) => row.codigo === "1")?.id ?? state.data[0]?.id ?? null;
      });
      setExpanded((current) => {
        if (current.size > 0) return current;
        return new Set(state.data.filter((row) => row.parent_id === null).map((row) => row.id));
      });
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo cargar el manual estructurado."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const byParent = useMemo(() => {
    const map = new Map<number | null, ManualSistemaIntegradoStructuredSection[]>();
    rows.forEach((row) => {
      const list = map.get(row.parent_id) ?? [];
      list.push(row);
      map.set(row.parent_id, list);
    });
    map.forEach((list) => list.sort((a, b) => a.orden - b.orden || a.id - b.id));
    return map;
  }, [rows]);

  const selected = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? null,
    [rows, selectedId],
  );

  const searchResults = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("es");
    if (!query) return [];
    return rows.filter((row) =>
      `${row.codigo ?? ""} ${row.titulo} ${row.contenido}`.toLocaleLowerCase("es").includes(query),
    );
  }, [rows, search]);

  const descendantIds = useMemo(() => {
    if (!editor.row) return new Set<number>();
    const result = new Set<number>();
    const visit = (id: number) => {
      (byParent.get(id) ?? []).forEach((child) => {
        if (result.has(child.id)) return;
        result.add(child.id);
        visit(child.id);
      });
    };
    visit(editor.row.id);
    return result;
  }, [byParent, editor.row]);

  const parentOptions = useMemo(
    () => rows.filter((row) => row.id !== editor.row?.id && !descendantIds.has(row.id)),
    [rows, editor.row?.id, descendantIds],
  );

  const openEditor = (
    row: ManualSistemaIntegradoStructuredSection | null,
    parentId: number | null = null,
  ) => {
    setEditorError(null);
    setEditor({ open: true, row, parentId });
    if (row) {
      setPayload({
        parent_id: row.parent_id,
        codigo: row.codigo ?? "",
        titulo: row.titulo,
        contenido: row.contenido,
        orden: row.orden,
      });
    } else {
      const siblings = rows.filter((item) => item.parent_id === parentId);
      const nextOrder = Math.max(0, ...siblings.map((item) => item.orden)) + 10;
      setPayload({ ...emptyPayload, parent_id: parentId, orden: nextOrder });
    }
  };

  const saveSection = async () => {
    if (!payload.titulo.trim()) return;
    setSaving(true);
    setEditorError(null);
    try {
      if (editor.row) {
        await manualSistemaIntegradoApi.updateStructuredSection(editor.row.id, {
          ...payload,
          titulo: payload.titulo.trim(),
          codigo: payload.codigo.trim(),
        });
      } else {
        await manualSistemaIntegradoApi.createStructuredSection({
          ...payload,
          titulo: payload.titulo.trim(),
          codigo: payload.codigo.trim(),
        });
      }
      setEditor({ open: false, row: null, parentId: null });
      await load(true);
    } catch (requestError) {
      setEditorError(errMsg(requestError, "No se pudo guardar el apartado del manual."));
    } finally {
      setSaving(false);
    }
  };

  const removeSection = async () => {
    if (!deleteSection) return;
    setDeletingSection(true);
    setError(null);
    try {
      await manualSistemaIntegradoApi.deleteStructuredSection(deleteSection.id);
      setDeleteSection(null);
      await load(true);
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo eliminar el apartado."));
    } finally {
      setDeletingSection(false);
    }
  };

  const uploadImage = async (file: File) => {
    if (!selected) return;
    setUploadingImage(true);
    setError(null);
    try {
      const updated = await manualSistemaIntegradoApi.uploadStructuredImage(selected.id, file);
      setRows((current) => current.map((row) => (row.id === updated.id ? updated : row)));
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo añadir la imagen al apartado."));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = async () => {
    if (!deleteImage) return;
    setDeletingImage(true);
    setError(null);
    try {
      await manualSistemaIntegradoApi.deleteStructuredImage(deleteImage.sectionId, deleteImage.image.id);
      setDeleteImage(null);
      await load(true);
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo eliminar la imagen."));
    } finally {
      setDeletingImage(false);
    }
  };

  const downloadPdf = async () => {
    setPdfDownloading(true);
    setError(null);
    try {
      const blob = await manualSistemaIntegradoApi.downloadStructuredPdf();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "manual-sistema-integrado-iso-9001-14001.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo generar el PDF del manual."));
    } finally {
      setPdfDownloading(false);
    }
  };

  const toggleExpanded = (id: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectSection = (row: ManualSistemaIntegradoStructuredSection) => {
    setSelectedId(row.id);
    let parentId = row.parent_id;
    setExpanded((current) => {
      const next = new Set(current);
      if ((byParent.get(row.id) ?? []).length > 0) {
        next.add(row.id);
      }
      while (parentId !== null) {
        next.add(parentId);
        parentId = rows.find((item) => item.id === parentId)?.parent_id ?? null;
      }
      return next;
    });
  };

  const renderTree = (parentId: number | null, depth = 0): ReactNode =>
    (byParent.get(parentId) ?? []).map((row) => {
      const children = byParent.get(row.id) ?? [];
      const isExpanded = expanded.has(row.id);
      return (
        <div key={row.id} className="manual-si-tree-node">
          <div className={`manual-si-tree-row ${selectedId === row.id ? "is-active" : ""}`} style={{ paddingLeft: `${depth * 14 + 6}px` }}>
            {children.length > 0 ? (
              <button
                type="button"
                className="manual-si-tree-toggle"
                aria-label={isExpanded ? "Contraer" : "Desplegar"}
                onClick={() => toggleExpanded(row.id)}
              >
                {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </button>
            ) : (
              <span className="manual-si-tree-spacer" />
            )}
            <button type="button" className="manual-si-tree-select" onClick={() => selectSection(row)}>
              {row.codigo ? <strong>{row.codigo}</strong> : null}
              <span>{row.titulo}</span>
            </button>
          </div>
          {children.length > 0 && isExpanded ? renderTree(row.id, depth + 1) : null}
        </div>
      );
    });

  return (
    <section className="manual-si-structured-card">
      <div className="manual-si-structured-toolbar">
        <div>
          <div className="iso27001-table-eyebrow">ISO 9001-14001</div>
          <h3>Manual estructurado</h3>
          <p>Navega, busca y mantiene el contenido del Manual sin sustituir el documento Word vigente.</p>
        </div>
        <div className="manual-si-structured-actions">
          <CertificationButton
            variant="secondary"
            icon={pdfDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            disabled={pdfDownloading || rows.length === 0}
            onClick={() => void downloadPdf()}
          >
            Generar PDF
          </CertificationButton>
          {canManage ? (
            <CertificationButton variant="primary" icon={<Plus size={16} />} onClick={() => openEditor(null)}>
              Añadir apartado
            </CertificationButton>
          ) : null}
        </div>
      </div>

      {error ? <div className="manual-si-structured-error" role="alert">{error}</div> : null}

      <div className="manual-si-structured-layout">
        <aside className="manual-si-index-panel">
          <div className="manual-si-index-heading">
            <div>
              <strong>Índice</strong>
              <span>{rows.length} puntos</span>
            </div>
            {refreshing ? <Loader2 size={15} className="animate-spin" /> : null}
          </div>
          <label className="manual-si-index-search">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar en el manual..." />
          </label>
          <div className="manual-si-index-scroll">
            {loading ? (
              <div className="manual-si-index-empty"><Loader2 size={18} className="animate-spin" /> Cargando manual...</div>
            ) : search.trim() ? (
              searchResults.length > 0 ? (
                <div className="manual-si-search-results">
                  {searchResults.map((row) => (
                    <button key={row.id} type="button" onClick={() => selectSection(row)}>
                      <strong>{row.codigo ?? "—"}</strong>
                      <span>{row.titulo}</span>
                    </button>
                  ))}
                </div>
              ) : <div className="manual-si-index-empty">Sin coincidencias.</div>
            ) : renderTree(null)}
          </div>
        </aside>

        <article className="manual-si-content-panel">
          {selected ? (
            <>
              <div className="manual-si-content-header">
                <div>
                  <div className="manual-si-content-code">{selected.codigo ?? "Apartado"}</div>
                  <h2>{selected.titulo}</h2>
                  {selected.usuario_modificacion_nombre ? (
                    <p>Última modificación por {selected.usuario_modificacion_nombre}</p>
                  ) : null}
                </div>
                {canManage ? (
                  <div className="manual-si-content-actions">
                    <CertificationButton variant="secondary" icon={<Edit3 size={15} />} onClick={() => openEditor(selected)}>
                      Editar
                    </CertificationButton>
                    <CertificationButton variant="secondary" icon={<Plus size={15} />} onClick={() => openEditor(null, selected.id)}>
                      Añadir subapartado
                    </CertificationButton>
                    <CertificationButton variant="ghost" icon={<Trash2 size={15} />} onClick={() => setDeleteSection(selected)}>
                      Eliminar
                    </CertificationButton>
                  </div>
                ) : null}
              </div>

              <div className="manual-si-markdown">
                {selected.contenido.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkManualTableColors]}>{selected.contenido}</ReactMarkdown>
                ) : (
                  <p className="manual-si-empty-content">Este punto actúa como agrupador y no tiene contenido propio.</p>
                )}
              </div>

              {selected.imagenes.length > 0 ? (
                <div className="manual-si-images-grid">
                  {selected.imagenes.map((image) => (
                    <figure key={image.id} className="manual-si-image-card">
                      <img
                        src={manualSistemaIntegradoApi.structuredImageUrl(selected.id, image.id)}
                        alt={image.pie || image.nombre_original}
                        loading="lazy"
                      />
                      <figcaption>
                        <span>{image.pie || image.nombre_original}</span>
                        {canManage ? (
                          <button type="button" aria-label="Eliminar imagen" onClick={() => setDeleteImage({ sectionId: selected.id, image })}>
                            <Trash2 size={15} />
                          </button>
                        ) : null}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : null}

              {canManage ? (
                <div className="manual-si-image-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void uploadImage(file);
                    }}
                  />
                  <CertificationButton
                    variant="secondary"
                    icon={uploadingImage ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
                    disabled={uploadingImage}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Añadir imagen
                  </CertificationButton>
                </div>
              ) : null}
            </>
          ) : (
            <div className="manual-si-content-empty">
              <FileText size={32} />
              <strong>Selecciona un punto del índice</strong>
              <span>Su contenido se mostrará aquí.</span>
            </div>
          )}
        </article>
      </div>

      <CertificationModal
        open={editor.open}
        title={editor.row ? "Editar apartado" : editor.parentId ? "Nuevo subapartado" : "Nuevo apartado"}
        eyebrow="Manual estructurado"
        description="Edita el contenido visualmente. Puedes dar formato al texto y gestionar tablas sin utilizar código."
        size="wide"
        onClose={() => setEditor({ open: false, row: null, parentId: null })}
        onSubmit={() => void saveSection()}
        submitting={saving}
        error={editorError}
        closeOnBackdrop={false}
        footer={
          <>
            <CertificationButton variant="secondary" disabled={saving} onClick={() => setEditor({ open: false, row: null, parentId: null })}>
              Cancelar
            </CertificationButton>
            <CertificationButton
              variant="primary"
              disabled={saving || !payload.titulo.trim()}
              icon={saving ? <Loader2 size={16} className="animate-spin" /> : <Edit3 size={16} />}
              type="submit"
            >
              Guardar
            </CertificationButton>
          </>
        }
      >
        <div className="manual-si-structured-form">
          <CertificationInputField
            label="Código"
            value={payload.codigo}
            onChange={(event) => setPayload((current) => ({ ...current, codigo: event.target.value }))}
            placeholder="Ej. 4.3.7"
            disabled={saving}
          />
          <CertificationInputField
            label="Orden"
            type="number"
            min={0}
            value={payload.orden}
            onChange={(event) => setPayload((current) => ({ ...current, orden: Number(event.target.value) || 0 }))}
            disabled={saving}
          />
          <CertificationInputField
            label="Título"
            value={payload.titulo}
            onChange={(event) => setPayload((current) => ({ ...current, titulo: event.target.value }))}
            disabled={saving}
            fullWidth
          />
          <CertificationSelectField
            label="Apartado padre"
            value={payload.parent_id ?? ""}
            onChange={(event) => setPayload((current) => ({ ...current, parent_id: event.target.value ? Number(event.target.value) : null }))}
            disabled={saving}
            fullWidth
          >
            <option value="">Sin apartado padre</option>
            {parentOptions.map((row) => <option key={row.id} value={row.id}>{sectionLabel(row)}</option>)}
          </CertificationSelectField>
          <div className="manual-si-structured-content-field">
            <div className="manual-si-structured-content-heading">
              <div>
                <strong>Contenido</strong>
                <span>Escribe y da formato directamente sobre el documento.</span>
              </div>
            </div>
            <ManualSistemaIntegradoVisualEditor
              value={payload.contenido}
              onChange={(contenido) => setPayload((current) => ({ ...current, contenido }))}
              disabled={saving}
            />
            <span className="manual-si-structured-content-hint">Las imágenes se gestionan desde el propio apartado una vez guardado.</span>
          </div>
        </div>
      </CertificationModal>

      <CertificationConfirmDialog
        open={deleteSection !== null}
        title="Eliminar apartado"
        description={deleteSection ? `Se eliminará “${sectionLabel(deleteSection)}”. Si contiene subapartados, la aplicación impedirá la eliminación.` : ""}
        confirming={deletingSection}
        onClose={() => setDeleteSection(null)}
        onConfirm={() => void removeSection()}
      />

      <CertificationConfirmDialog
        open={deleteImage !== null}
        title="Eliminar imagen"
        description={deleteImage ? `Se eliminará la imagen “${deleteImage.image.pie || deleteImage.image.nombre_original}”.` : ""}
        confirming={deletingImage}
        onClose={() => setDeleteImage(null)}
        onConfirm={() => void removeImage()}
      />
    </section>
  );
}
